import {
  Checkbox,
  Grid,
  PasswordInput,
  Select,
  Space,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { useEffect } from 'react';
import { BillingCycleData } from '../../../../data/billing-cycle.data.array';
import { CurrenciesData } from '../../../../data/currencies-data.array';
import { CreateMerchantDetailsForm } from '../../../../type/entity.types';
import {
  EntityFilterData,
  EntityIdFilterData,
} from '../../../../type/filter.types';
import SelectPartnerFilter from '../../../filters/select-partner-filter.component';
import SelectResellerFilter from '../../../filters/select-reseller-filter.component';
import XCol4 from '../../../x-components/grid-col/x-col-4.component';
import XCol6 from '../../../x-components/grid-col/x-col-6.component';

function MerchantDetails(props: Props) {
  const { form, isAdmin, isPartner } = props;

  const filterData = useForm<EntityFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      partner: undefined,
      reseller: undefined,
    },
  });

  useEffect(() => {
    form.setFieldValue('partnerId', filterValues.values.partner);
    form.setFieldValue('resellerId', filterValues.values.reseller);
  }, [filterValues.values]);

  return (
    <>
      <Text size='xl' weight={700}>
        Merchant Details
      </Text>
      <Space h='xl' />
      <Grid>
        {isAdmin && (
          <XCol6>
            <SelectPartnerFilter
              filterSelectData={filterData}
              filterValues={filterValues}
              error={form.errors.partnerId}
            />
          </XCol6>
        )}

        {(isPartner || isAdmin) && (
          <>
            <XCol6>
              <SelectResellerFilter
                filterSelectData={filterData}
                filterValues={filterValues}
                isPartnerDependent={isAdmin}
                error={form.errors.resellerId}
              />
            </XCol6>
            {isPartner && <XCol6></XCol6>}
          </>
        )}
        <XCol6>
          <TextInput
            label={labels.entityName}
            placeholder={placeholders.entityName}
            {...form.getInputProps('entityName')}
          />
        </XCol6>
        <XCol6>
          <TextInput
            label={labels.emailAddress}
            placeholder={placeholders.emailAddress}
            {...form.getInputProps('email')}
          />
        </XCol6>
        <XCol4>
          <TextInput
            label={labels.firstName}
            placeholder={placeholders.firstName}
            {...form.getInputProps('firstName')}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.middleName}
            placeholder={placeholders.middleName}
            disabled={form.values.noMiddleName}
            {...form.getInputProps('middleName')}
          />
          <Space my='xs' />
          <Checkbox
            label="I don't have a middle name"
            checked={form.values.noMiddleName}
            onChange={() => {
              const noMiddleName = !form.values.noMiddleName;
              form.setFieldValue('noMiddleName', noMiddleName);
              form.setFieldValue('middleName', noMiddleName ? '.' : '');
            }}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.lastName}
            placeholder={placeholders.lastName}
            {...form.getInputProps('lastName')}
          />
        </XCol4>

        <XCol4>
          <Select
            label={labels.billingCycle}
            placeholder={placeholders.billingCycle}
            data={BillingCycleData}
            value={form.values.billingCycleId?.toString()}
            onChange={(value: string) => {
              form.setFieldValue('billingCycleId', parseInt(value));
            }}
            error={form.errors.billingCycleId}
          />
        </XCol4>
        <XCol4>
          <Select
            label={labels.currency}
            placeholder={placeholders.currency}
            data={CurrenciesData}
            value={form.values.currency?.toString()}
            onChange={(value: string) => {
              form.setFieldValue('currency', parseInt(value));
            }}
            error={form.errors.currency}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.username}
            placeholder={placeholders.username}
            {...form.getInputProps('username')}
          />
        </XCol4>
        <XCol4>
          <PasswordInput
            label={labels.password}
            placeholder={placeholders.password}
            {...form.getInputProps('password')}
          />
        </XCol4>
        <XCol4>
          <PasswordInput
            label={labels.confirmPassword}
            placeholder={placeholders.confirmPassword}
            {...form.getInputProps('confirmPassword')}
          />
        </XCol4>
        <XCol4>
          <PasswordInput
            label={labels.pin}
            placeholder={placeholders.pin}
            {...form.getInputProps('pin')}
            maxLength={4}
          />
        </XCol4>
      </Grid>
    </>
  );
}

export default MerchantDetails;

interface Props {
  form: UseFormReturnType<CreateMerchantDetailsForm>;
  isAdmin: boolean;
  isPartner: boolean;
}

const labels = {
  entityName: 'Merchant name',
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  emailAddress: 'Merchant email',
  billingCycle: 'Billing cycle',
  currency: 'Currency',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'PIN',
};
const placeholders = {
  entityName: 'Merchant name',
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  emailAddress: 'Merchant email',
  billingCycle: 'Billing cycle',
  currency: 'Currency',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'PIN',
};
